import { useState } from 'react';

// ── Spinner ──────────────────────────────────────────────────────────────────
export function Spinner({ size = 20, color = 'var(--accent)' }) {
  return (
    <div style={{
      width: size, height: size, border: `2px solid ${color}22`,
      borderTop: `2px solid ${color}`, borderRadius: '50%',
      animation: 'spin .7s linear infinite', flexShrink: 0,
    }}/>
  );
}

// ── Badge ────────────────────────────────────────────────────────────────────
const badgeStyles = {
  active:     { bg: '#d8f3dc', color: '#1b4332' },
  pending:    { bg: '#fef9ec', color: '#7d5a00' },
  suspended:  { bg: '#fdf0ee', color: '#7b2d26' },
  published:  { bg: '#d8f3dc', color: '#1b4332' },
  draft:      { bg: '#f0f0f0', color: '#555' },
  archived:   { bg: '#fdf0ee', color: '#7b2d26' },
  sent:       { bg: '#d8f3dc', color: '#1b4332' },
  open:       { bg: '#eaf4fb', color: '#1a5276' },
  in_review:  { bg: '#fef9ec', color: '#7d5a00' },
  resolved:   { bg: '#d8f3dc', color: '#1b4332' },
  text:       { bg: '#eaf4fb', color: '#1a5276' },
  voice:      { bg: '#f3e8ff', color: '#5b21b6' },
  both:       { bg: '#fef9ec', color: '#7d5a00' },
  all:        { bg: '#f0faf2', color: '#2d6a4f' },
  role_specific: { bg: '#f3e8ff', color: '#5b21b6' },
};

export function Badge({ label, type }) {
  const s = badgeStyles[type] || badgeStyles[label?.toLowerCase()] || { bg: '#f0f0f0', color: '#555' };
  return (
    <span style={{
      display: 'inline-block', fontSize: 11, fontWeight: 500,
      padding: '2px 9px', borderRadius: 20,
      background: s.bg, color: s.color, whiteSpace: 'nowrap',
    }}>{label}</span>
  );
}

// ── Button ───────────────────────────────────────────────────────────────────
export function Btn({ children, variant = 'primary', size = 'md', onClick, disabled, style = {}, type = 'button' }) {
  const base = {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    fontFamily: 'var(--sans)', fontWeight: 500, cursor: disabled ? 'not-allowed' : 'pointer',
    border: 'none', borderRadius: 'var(--r)', transition: 'all .15s',
    opacity: disabled ? .5 : 1, whiteSpace: 'nowrap',
    ...(size === 'sm' ? { fontSize: 12, padding: '5px 12px' } : { fontSize: 13, padding: '8px 16px' }),
  };
  const variants = {
    primary:  { background: 'var(--accent)',  color: '#fff' },
    danger:   { background: 'var(--red)',     color: '#fff' },
    ghost:    { background: 'transparent',    color: 'var(--text2)', border: '1px solid var(--border)' },
    outline:  { background: 'transparent',    color: 'var(--accent)', border: '1px solid var(--accent)' },
    gold:     { background: 'var(--gold)',    color: '#fff' },
  };
  return (
    <button type={type} style={{ ...base, ...variants[variant], ...style }}
      onClick={onClick} disabled={disabled}>
      {children}
    </button>
  );
}

// ── Input ────────────────────────────────────────────────────────────────────
export function Input({ label, error, style = {}, ...props }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {label && <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text2)' }}>{label}</label>}
      <input style={{
        background: 'var(--surface)', border: `1px solid ${error ? 'var(--red)' : 'var(--border)'}`,
        borderRadius: 'var(--r)', padding: '8px 12px', fontSize: 13,
        color: 'var(--text)', outline: 'none', width: '100%', ...style,
      }} {...props}/>
      {error && <span style={{ fontSize: 11, color: 'var(--red)' }}>{error}</span>}
    </div>
  );
}

// ── Select ───────────────────────────────────────────────────────────────────
export function Select({ label, children, options, style = {}, ...props }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {label && <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text2)' }}>{label}</label>}
      <select style={{
        background: 'var(--surface)', border: '1px solid var(--border)',
        borderRadius: 'var(--r)', padding: '8px 12px', fontSize: 13,
        color: 'var(--text)', outline: 'none', width: '100%', ...style,
      }} {...props}>
        {options
          ? options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)
          : children}
      </select>
    </div>
  );
}

// ── Card ─────────────────────────────────────────────────────────────────────
export function Card({ children, style = {}, onClick }) {
  return (
    <div onClick={onClick} style={{
      background: 'var(--surface)', borderRadius: 'var(--r2)',
      border: '1px solid var(--border)', boxShadow: 'var(--shadow)',
      animation: 'fadeIn .3s ease', cursor: onClick ? 'pointer' : 'default',
      ...style,
    }}>{children}</div>
  );
}

// ── Modal ────────────────────────────────────────────────────────────────────
export function Modal({ title, children, onClose, width = 480 }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: 20,
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{
        background: 'var(--surface)', borderRadius: 'var(--r3)',
        width: '100%', maxWidth: width, maxHeight: '90vh',
        overflow: 'auto', animation: 'fadeIn .2s ease',
        boxShadow: 'var(--shadow2)',
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 20px', borderBottom: '1px solid var(--border)',
        }}>
          <h3 style={{ fontFamily: 'var(--serif)', fontSize: 18, fontWeight: 700 }}>{title}</h3>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', fontSize: 20,
            color: 'var(--text3)', cursor: 'pointer', lineHeight: 1,
          }}>×</button>
        </div>
        <div style={{ padding: 20 }}>{children}</div>
      </div>
    </div>
  );
}

// ── Table ────────────────────────────────────────────────────────────────────
export function Table({ columns, data, loading, emptyMsg = 'No records found' }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr>
            {columns.map(c => (
              <th key={c.key} style={{
                padding: '10px 14px', textAlign: 'left', fontWeight: 600,
                fontSize: 11, letterSpacing: '.06em', textTransform: 'uppercase',
                color: 'var(--text3)', borderBottom: '1px solid var(--border)',
                whiteSpace: 'nowrap', background: 'var(--surface2)',
              }}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan={columns.length} style={{ padding: 40, textAlign: 'center' }}>
              <Spinner/>
            </td></tr>
          ) : data.length === 0 ? (
            <tr><td colSpan={columns.length} style={{ padding: 40, textAlign: 'center', color: 'var(--text3)' }}>
              {emptyMsg}
            </td></tr>
          ) : data.map((row, i) => (
            <tr key={i} style={{ borderBottom: '1px solid var(--border)', transition: 'background .1s' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--surface2)'}
              onMouseLeave={e => e.currentTarget.style.background = ''}>
              {columns.map(c => (
                <td key={c.key} style={{ padding: '10px 14px', color: 'var(--text)', verticalAlign: 'middle' }}>
                  {c.render ? c.render(row[c.key], row) : row[c.key] ?? '—'}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Pagination ────────────────────────────────────────────────────────────────
export function Pagination({ page, pages, total, limit, onChange }) {
  if (pages <= 1) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderTop: '1px solid var(--border)' }}>
      <span style={{ fontSize: 12, color: 'var(--text3)' }}>
        {(page - 1) * limit + 1}–{Math.min(page * limit, total)} of {total}
      </span>
      <div style={{ display: 'flex', gap: 4 }}>
        {Array.from({ length: pages }, (_, i) => i + 1).map(p => (
          <button key={p} onClick={() => onChange(p)} style={{
            width: 28, height: 28, borderRadius: 6, border: '1px solid var(--border)',
            background: p === page ? 'var(--accent)' : 'transparent',
            color: p === page ? '#fff' : 'var(--text2)', fontSize: 12, fontWeight: 500,
            cursor: 'pointer',
          }}>{p}</button>
        ))}
      </div>
    </div>
  );
}

// ── Toast ────────────────────────────────────────────────────────────────────
export function Toast({ message, type = 'success', onClose }) {
  const colors = { success: 'var(--accent)', error: 'var(--red)', info: 'var(--blue)' };
  return (
    <div style={{
      position: 'fixed', bottom: 24, right: 24, zIndex: 2000,
      background: 'var(--surface)', border: `1px solid var(--border)`,
      borderLeft: `4px solid ${colors[type]}`, borderRadius: 'var(--r2)',
      padding: '12px 16px', boxShadow: 'var(--shadow2)',
      display: 'flex', alignItems: 'center', gap: 12,
      animation: 'fadeIn .25s ease', minWidth: 260,
    }}>
      <span style={{ fontSize: 13, color: 'var(--text)', flex: 1 }}>{message}</span>
      <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', fontSize: 16 }}>×</button>
    </div>
  );
}

// ── useToast hook ─────────────────────────────────────────────────────────────
export function useToast() {
  const [toast, setToast] = useState(null);
  function show(message, type = 'success') {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  }
  const ToastEl = toast ? <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)}/> : null;
  return { show, ToastEl };
}

// ── Stat card ─────────────────────────────────────────────────────────────────
export function StatCard({ label, value, sub, icon, color = 'var(--accent)' }) {
  return (
    <Card style={{ padding: '20px 24px', display: 'flex', alignItems: 'flex-start', gap: 16 }}>
      <div style={{
        width: 44, height: 44, borderRadius: 10, background: color + '18',
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color,
      }}>
        {icon}
      </div>
      <div>
        <div style={{ fontSize: 26, fontWeight: 600, lineHeight: 1.1, fontFamily: 'var(--mono)' }}>{value ?? '—'}</div>
        <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 2 }}>{label}</div>
        {sub && <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{sub}</div>}
      </div>
    </Card>
  );
}
